"use client";

import { motion, useReducedMotion } from "framer-motion";

import { PANEL_WIDTH_PX, TRACE_EDGE } from "@/components/learn-piece/shell/panel-metrics";
import { MiniPiano } from "@/components/ui/molecules/mini-piano";
import { HAND_LANE_Y, HandArc } from "@/components/learn-piece/shell/keyboard-marks";
import { KEYBOARD_VIEWBOX_HEIGHT, keyCenterX, keyboardViewBoxWidth } from "@/components/ui/molecules/mini-piano";
import { contractOf } from "@/lib/levels/words";
import type { Level } from "@/lib/levels/client";

/**
 * La couture : le seul endroit de l'échelle où les mains changent de place.
 *
 * ## Le mouvement est l'explication, et il n'y en a qu'un
 *
 * Deux claviers côte à côte, **même fenêtre de hauteurs**, avec les arcs de
 * main. À l'ouverture, l'arc de la main gauche quitte la fondamentale du modèle
 * pour la note que la partition écrit ; celui de la main droite **ne bouge
 * pas**. Un texte seul aurait demandé au joueur de se figurer un déplacement ;
 * le déplacement se montre, et le texte confirme.
 *
 * ## Pourquoi la droite ne bouge plus ici
 *
 * Elle montait d'une octave à cette couture, pour sortir des touches que la
 * main gauche de la partition vient prendre. C'était le bon calcul au mauvais
 * moment : les deux mains changeaient au même barreau, et l'échelle tient
 * précisément parce qu'une couture n'en change qu'une. La levée est désormais
 * décidée avant le premier niveau, une fois pour le morceau, sur les touches
 * réellement disputées. Mesuré sur 40 morceaux et 3 684 fenêtres : la main
 * droite change à cette couture sur **zéro** d'entre elles, contre 93 % avant.
 * Montrer deux mains qui bougent enseignerait donc le contraire de ce qui se
 * passe.
 *
 * ## Le texte est déjà écrit, et il est juste
 *
 * Mot pour mot celui de `contractOf()` sur un niveau échafaudé. Le réécrire
 * ici aurait fait deux versions d'un contrat qui n'en supporte pas deux : c'est
 * la seule chose qui sépare un modèle assumé d'un mensonge sur la partition.
 */

const OCTAVES = 2;
const LOW_MIDI = 48;

export function SeamScreen({
  level,
  onStart,
}: {
  readonly level: Pick<Level, "realisation" | "scaffolded">;
  readonly onStart: () => void;
}) {
  const reducedMotion = useReducedMotion() ?? false;

  return (
    <div
      className="flex h-full items-center justify-center"
      style={{ padding: 24 }}
      role="dialog"
      aria-label="La main gauche prend la partition, la droite ne bouge pas"
    >
      <div style={{ width: PANEL_WIDTH_PX, maxWidth: "100%", textAlign: "center" }}>
        <h2 style={{ margin: 0, fontSize: 18, color: "var(--color-text-primary)" }}>
          La main gauche prend la partition, la droite ne bouge pas
        </h2>

        <div className="flex" style={{ gap: 20, marginTop: 20 }}>
          <Panel label="AVANT" reducedMotion={reducedMotion} moved={false} />
          <Panel label="MAINTENANT" reducedMotion={reducedMotion} moved />
        </div>

        <p
          style={{
            margin: "20px 0 0",
            fontSize: 13,
            lineHeight: 1.65,
            color: "var(--color-text-soft)",
            textAlign: "left",
          }}
        >
          {contractOf(level as Level)}
        </p>

        <button
          type="button"
          onClick={onStart}
          className="transition-colors hover:bg-white/[0.10]"
          style={{
            marginTop: 20,
            padding: "9px 20px",
            borderRadius: 8,
            border: "1px solid var(--color-border-subtle)",
            background: "rgba(255, 255, 255, 0.06)",
            color: "var(--color-text-primary)",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Commencer
        </button>
      </div>
    </div>
  );
}

/**
 * Un des deux états, sur la même fenêtre que l'autre.
 *
 * La fenêtre partagée est ce qui rend la comparaison lisible : deux claviers
 * cadrés différemment feraient bouger les mains sans qu'elles se déplacent.
 */
function Panel({
  label,
  moved,
  reducedMotion,
}: {
  readonly label: string;
  readonly moved: boolean;
  readonly reducedMotion: boolean;
}) {
  const viewWidth = keyboardViewBoxWidth(OCTAVES);

  // Avant : la gauche tient la fondamentale de l'accord, la droite la triade au
  // dessus. Après : la gauche va chercher la note que la partition écrit, qui
  // n'est la fondamentale que dans la moitié des cas. La droite garde les mêmes
  // touches, et c'est le fait à montrer.
  const left = moved ? [LOW_MIDI + 2] : [LOW_MIDI + 7];
  const right = [LOW_MIDI + 12, LOW_MIDI + 16];

  const span = (midis: readonly number[]) => {
    const xs = midis.map((midi) => keyCenterX(midi, LOW_MIDI, OCTAVES)).filter((x): x is number => x !== null);

    return xs.length === 0 ? null : { x1: Math.min(...xs), x2: Math.max(...xs) };
  };

  const leftSpan = span(left);
  const rightSpan = span(right);

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        className="font-mono uppercase"
        style={{ fontSize: 10, letterSpacing: "0.18em", color: "var(--color-text-muted)" }}
      >
        {label}
      </div>

      <div style={{ position: "relative", marginTop: 8 }}>
        <MiniPiano
          midis={[...left, ...right]}
          lowMidi={LOW_MIDI}
          octaves={OCTAVES}
          midiColors={
            new Map([
              ...left.map((m) => [m, "var(--color-hand-left)"] as const),
              ...right.map((m) => [m, "var(--color-hand-right)"] as const),
            ])
          }
          style={{ width: "100%", height: "auto" }}
        />

        <svg
          viewBox={`0 0 ${viewWidth} ${KEYBOARD_VIEWBOX_HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden
          style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
        >
          {/* Les arcs se déplacent, ils n'apparaissent pas : c'est le
              déplacement qui explique, et une apparition ne déplace rien. */}
          {leftSpan !== null && (
            <motion.g
              layoutId={`seam-arc-left-${label}`}
              initial={reducedMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={reducedMotion ? { duration: 0 } : { duration: 0.6, ease: "easeOut" }}
            >
              <HandArc x1={leftSpan.x1} x2={leftSpan.x2} y={HAND_LANE_Y.L} color="var(--color-hand-left)" />
            </motion.g>
          )}

          {rightSpan !== null && (
            <motion.g
              layoutId={`seam-arc-right-${label}`}
              initial={reducedMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={reducedMotion ? { duration: 0 } : { duration: 0.6, ease: "easeOut" }}
            >
              <HandArc x1={rightSpan.x1} x2={rightSpan.x2} y={HAND_LANE_Y.R} color="var(--color-hand-right)" />
            </motion.g>
          )}
        </svg>
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 11,
          lineHeight: 1.4,
          color: "var(--color-text-soft)",
          borderTop: `1px solid ${TRACE_EDGE}`,
          paddingTop: 6,
        }}
      >
        {moved ? "la partition, et la droite au même endroit" : "le modèle, à sa place"}
      </div>
    </div>
  );
}
